<template>
    <div class="">
        {{ $t('messages.selected_contacts') }}: <span class="text-2xl">{{ recipientsIds.length }}</span>

        <div class="sm:col-span-4">
            <label for="search_student" class="block text-sm font-medium leading-5 text-gray-700"></label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    id="search_student"
                    v-model="search"
                    type="text"
                    :placeholder="$t('messages.show_only')"
                    class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                />
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </div>
            </div>
        </div>

        <div v-for="group in groups" class="mt-3" :key="group.id">
            <div
                class="bg-gray-200 flex items-center justify-between py-1 px-2 cursor-pointer"
                @click="toggleGroup(group)"
            >
                <div>
                    {{ group.name }}
                </div>
                <svg
                    class="-mr-1 ml-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    :class="collapsed.includes(group.id) ? '-rotate-90' : ''"
                >
                    <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                    />
                </svg>
            </div>

            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
                <div v-show="collapsed.includes(group.id)" class="mt-1">
                    <div>
                        <SelectAllNoneButtons
                            :max-count="2"
                            :selected-count="1"
                            @select-all="selectAll(group)"
                            @select-none="deselectAll(group)"
                            class="flex"
                        />
                    </div>
                    <div
                        v-for="student in group.students"
                        v-show="filteredStudentsIds.includes(student.id)"
                        :key="student.id"
                    >
                        <div class="text-gray-500 cursor-pointer" @click="toggleRelatives(student)">
                            {{ student.full_name }}
                        </div>
                        <ul class="list-inside" v-if="student.direct_relatives.length">
                            <li
                                class="ml-3 flex items-center space-x-2"
                                v-for="relative in student.direct_relatives"
                                :key="relative.id"
                            >
                                <label class="flex gap-1 items-center">
                                    <input
                                        type="checkbox"
                                        v-model="recipientsIds"
                                        name="recipients_ids[]"
                                        :value="relative.id"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                    <span class="pl-1">{{ relative.full_name }}</span>
                                    <span class="text-sm text-gray-600">({{ relative.relationship }})</span>
                                </label>
                            </li>
                        </ul>
                        <div v-else class="ml-3 text-gray-400">
                            {{ $t('messages.no_contacts') }}
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

export default {
    emits: ['update:modelValue'],
    data() {
        return {
            recipientsIds: this.modelValue,
            collapsed: [],
            search: '',
        };
    },
    components: {
        SelectAllNoneButtons,
    },
    props: {
        groups: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        toggleGroup(group) {
            if (this.collapsed.includes(group.id)) {
                this.collapsed.splice(this.collapsed.indexOf(group.id), 1);
            } else {
                this.collapsed.push(group.id);
            }
        },
        selectAll(group) {
            group.students.forEach((s) => {
                this.selectRelatives(s);
            });
        },
        deselectAll(group) {
            group.students.forEach((s) => {
                this.deselectRelatives(s);
            });
        },
        selectRelatives(student) {
            const relativesIds = student.direct_relatives.map((p) => p.id);
            relativesIds.forEach((p) => {
                if (!this.recipientsIds.includes(p)) {
                    this.recipientsIds.push(p);
                }
            });
        },
        deselectRelatives(student) {
            const relativesIds = student.direct_relatives.map((p) => p.id);
            relativesIds.forEach((p) => {
                this.recipientsIds.splice(this.recipientsIds.indexOf(p), 1);
            });
        },
        toggleRelatives(student) {
            if (!student.direct_relatives.length) {
                return;
            }

            const relativesIds = student.direct_relatives.map((p) => p.id);

            const containsAllRelatives = relativesIds.every((p) => this.recipientsIds.includes(p));

            if (containsAllRelatives) {
                this.deselectRelatives(student);
            } else {
                this.selectRelatives(student);
            }
        },
    },
    computed: {
        filteredStudentsIds: function () {
            if (this.search.trim() === '') {
                return this.groups
                    .map((g) => g.students)
                    .flat()
                    .map((s) => s.id);
            }

            const searchUpperCase = this.search.toUpperCase();

            return this.groups
                .map((g) => g.students)
                .flat()
                .filter((s) => {
                    if (s.full_name.toUpperCase().indexOf(searchUpperCase) !== -1) {
                        return true;
                    }
                })
                .map((s) => s.id);
        },
    },
    watch: {
        recipientsIds(newVal) {
            this.$emit('update:modelValue', newVal);
        },
    },
};
</script>
